import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { useParams } from 'react-router-dom';
import Loading from '@/components/general/Loading';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import checkinReports, {
  PostCheckinApi,
  GetUserCheckinsSummaryProps,
  UpdatePriorityStatus,
} from '@/services/checkinReports';
import { FetchApiIds, FormErrors } from '@/types';
import { AnswerTypes, Question, UserCheckin, CheckinSummary, Answer } from '@/types/api';
import { formatCheckinHistory } from '@/utils/checkins';
import UserCurrentCheckinComponent from './UserCurrentCheckin.component';

export type UnansweredQuestionType = Question & { answer: string | string[] };
const UserCurrentCheckinContainer = function (): JSX.Element {
  const { checkinId } = useParams();
  const toast = useToastContext();

  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const [questions, setQuestions] = useState<UnansweredQuestionType[]>([]);
  const [prioritiesFromLastWeek, setPrioritiesFromLastWeek] = useState<Answer[]>([]);

  const currentCheckinMutation = useApiSubmit<Question[], PostCheckinApi>(
    checkinReports.submitCheckin,
    {
      onError: () => {
        toast.setMessage(FormErrors.GENERIC);
        toast.setSeverity('error');
        toast.showToast();
      },
    }
  );

  const currentCheckinResponse = useApiFetch<UserCheckin>(
    [FetchApiIds.CHECKIN_BY_ID, checkinId],
    async () => checkinReports.getCheckinById({ checkinId: checkinId as string })
  );

  const userCheckinsResponse = useApiFetch<CheckinSummary[], GetUserCheckinsSummaryProps>(
    FetchApiIds.USER_CHECKINS_SUMMARY,
    checkinReports.getAllUsersCheckins
  );

  const updatePriorityStatusMutation = useApiSubmit<void, UpdatePriorityStatus>(
    checkinReports.updatePriorityStatus
  );

  const handleUpdatePriorityStatus = async (
    answerId: string,
    isPriorityFinished: boolean
  ): Promise<void> => {
    if (checkinId && answerId) {
      await updatePriorityStatusMutation.mutateAsync({
        checkinId,
        answerId,
        isFinished: isPriorityFinished,
      });
      const prioritiesFromLastWeekUpdated = prioritiesFromLastWeek.map((priority) => {
        if (priority.id === answerId) {
          return {
            ...priority,
            isFinished: isPriorityFinished,
          };
        }
        return priority;
      });
      setPrioritiesFromLastWeek(prioritiesFromLastWeekUpdated);
    }
  };

  useEffect(() => {
    if (currentCheckinResponse.data?.questions) {
      const questionsFilteredAndInitialized = currentCheckinResponse.data.questions
        .filter(Boolean)
        .map((data) => {
          const isArrayOrPriotityType =
            data?.answerType === AnswerTypes.ARRAY || data?.answerType === AnswerTypes.PRIORITY;

          return {
            ...data,
            answer: isArrayOrPriotityType ? [''] : '',
          };
        });
      setQuestions(questionsFilteredAndInitialized);

      if (currentCheckinResponse.data?.prioritiesFromLastWeek) {
        setPrioritiesFromLastWeek(currentCheckinResponse.data?.prioritiesFromLastWeek);
      }
    }
  }, [currentCheckinResponse.data]);

  const areAllRequiredQuestionsAnswered = useMemo(() => {
    const requiredQuestions = questions.filter((question) => question.isRequired);
    const stringQuestions = requiredQuestions.filter(
      ({ answerType }) => answerType === AnswerTypes.STRING
    );
    const numberQuestions = requiredQuestions.filter(
      ({ answerType }) => answerType === AnswerTypes.NUMBER
    );

    const priorityQuestions = requiredQuestions.filter(
      ({ answerType }) => answerType === AnswerTypes.PRIORITY
    );

    const arrayQuestions = requiredQuestions.filter(
      ({ answerType }) => answerType === AnswerTypes.ARRAY
    );

    const wasAnswered = (question): boolean => !!question.answer;
    const containsAnAnswer = ({ answer }): boolean => {
      return typeof answer !== 'string' && !!answer.filter(Boolean).length;
    };

    const areStringQuestionsAnswered = stringQuestions.every(wasAnswered);
    const areNumberQuestionsAnswered = numberQuestions.every(wasAnswered);
    const arePriorityQuestionsAnswered = priorityQuestions.every(containsAnAnswer);
    const areArrayQuestionsAnswered = arrayQuestions.every(containsAnAnswer);

    return (
      areStringQuestionsAnswered &&
      areNumberQuestionsAnswered &&
      arePriorityQuestionsAnswered &&
      areArrayQuestionsAnswered
    );
  }, [questions]);

  const updateQuestionOfArrayType = (
    stateQuestion: Question & { answer: string[] },
    index: string,
    value: string
  ): UnansweredQuestionType => {
    const updatedArrayItem = stateQuestion.answer.map((answers, answerIndex) => {
      if (answerIndex !== parseInt(index, 10)) {
        return answers;
      }
      return value;
    });

    return {
      ...stateQuestion,
      answer: updatedArrayItem,
    };
  };

  const updateQuestionOfStringType = (
    stateQuestion: UnansweredQuestionType,
    value: string
  ): UnansweredQuestionType => {
    return {
      ...stateQuestion,
      answer: value,
    };
  };

  const updateQuestions = useCallback(({ stateQuestion, id, index, value }) => {
    const isNotTheRightQuestion = stateQuestion.id !== id;
    const isArrayOrPriotityType =
      stateQuestion.answerType === AnswerTypes.ARRAY ||
      stateQuestion.answerType === AnswerTypes.PRIORITY;
    const isAnswerAnArray = typeof stateQuestion.answer !== 'string';

    if (isNotTheRightQuestion) {
      return stateQuestion;
    }

    if (isArrayOrPriotityType && isAnswerAnArray) {
      return updateQuestionOfArrayType(stateQuestion, index, value);
    }

    return updateQuestionOfStringType(stateQuestion, value);
  }, []);

  const onAnswerHandler = useCallback(
    (
      event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      id: string,
      index?: number
    ): void => {
      const {
        target: { value },
      } = event;
      setQuestions((prevState) => {
        return prevState.map((stateQuestion) =>
          updateQuestions({ stateQuestion, id, index, value })
        );
      });
    },
    [updateQuestions]
  );

  const addArrayItem = useCallback((id: string): void => {
    setQuestions((prevState) => {
      return prevState.map((stateItem) => {
        const isTheSameQuestion = stateItem.id === id;
        const isAnswerNotAString = typeof stateItem.answer !== 'string';
        if (isTheSameQuestion && isAnswerNotAString) {
          return {
            ...stateItem,
            answer: stateItem.answer.concat(''),
          };
        }
        return stateItem;
      });
    });
  }, []);

  const priorityQuestion = useMemo(
    (): UnansweredQuestionType | undefined =>
      questions.find((question) => question.answerType === AnswerTypes.PRIORITY),
    [questions]
  );

  const onCopyPriorityHandler = useCallback(
    (value): void => {
      const priorityQuestionId = priorityQuestion?.id;
      const answer = priorityQuestion?.answer;
      const isAnswerAnArray = typeof answer !== 'string' && !!answer?.length;
      const isLastAnswerEmpty = isAnswerAnArray && answer[answer.length - 1] === '';
      const newAnswerIndex = isLastAnswerEmpty ? answer?.length - 1 : answer?.length;

      if (!isLastAnswerEmpty && priorityQuestionId) {
        addArrayItem(priorityQuestionId);
      }
      setQuestions((prevState) => {
        return prevState.map((stateQuestion) =>
          updateQuestions({ stateQuestion, id: priorityQuestionId, index: newAnswerIndex, value })
        );
      });
    },
    [addArrayItem, updateQuestions, priorityQuestion]
  );

  const removeArrayItem = useCallback((index: number, id: string): void => {
    setQuestions((prevState) => {
      return prevState.map((stateItem) => {
        const isTheSameQuestion = stateItem.id === id;
        if (isTheSameQuestion && typeof stateItem.answer !== 'string') {
          const answersWithoutRemovedItem = stateItem.answer.filter((_answer, i) => i !== index);
          return {
            ...stateItem,
            answer: answersWithoutRemovedItem,
          };
        }
        return stateItem;
      });
    });
  }, []);

  const getSplittedPriorityAnswers = useCallback((): {
    description: string;
    answer: string | string[];
  }[] => {
    const splitedPriorities: {
      description: string;
      answer: string | string[];
    }[] = [];

    if (priorityQuestion && Array.isArray(priorityQuestion?.answer)) {
      const { answer, label: description } = priorityQuestion;
      answer.filter(Boolean).forEach((priotityLabel) => {
        splitedPriorities.push({
          description,
          answer: priotityLabel,
        });
      });
    }
    return splitedPriorities;
  }, [priorityQuestion]);

  const onSubmitHandler = useCallback(async () => {
    try {
      setIsFormSubmitted(true);
      toast.closeToast();
      if (!areAllRequiredQuestionsAnswered) {
        toast.setMessage(FormErrors.MISSING_REQUIRED_FIELDS);
        toast.setSeverity('error');
        toast.showToast();
        return;
      }

      const splittedPriorityAnswers = getSplittedPriorityAnswers();

      let answeredQuestions = questions
        .filter((question) => question.answerType !== AnswerTypes.PRIORITY)
        .map(({ answer, label: description }) => ({
          description,
          answer: typeof answer === 'string' ? answer : answer.filter(Boolean),
        }));

      answeredQuestions = [...answeredQuestions, ...splittedPriorityAnswers];

      const payload = {
        answers: answeredQuestions,
        id: checkinId as string,
      };

      await currentCheckinMutation.mutateAsync(payload);
      toast.setMessage('The checkin has been submitted.');
      toast.setSeverity('success');
      toast.showToast();
    } catch (err) {
      toast.setMessage('The checkin has been submitted.');
      toast.setSeverity('success');
      toast.showToast();
    }
  }, [
    areAllRequiredQuestionsAnswered,
    checkinId,
    currentCheckinMutation,
    getSplittedPriorityAnswers,
    questions,
    toast,
  ]);

  useEffect(() => {
    if (areAllRequiredQuestionsAnswered) {
      toast.closeToast();
    }
  }, [areAllRequiredQuestionsAnswered, toast]);

  const checkinsHistory = useMemo(() => {
    const allUserCheckins = userCheckinsResponse.data ?? [];
    return formatCheckinHistory(allUserCheckins);
  }, [userCheckinsResponse.data]);

  const isPageLoading = currentCheckinResponse.isLoading || userCheckinsResponse.isLoading;

  if (isPageLoading) return <Loading fullPage />;

  return (
    <UserCurrentCheckinComponent
      checkinsHistory={checkinsHistory}
      removeOneLine={removeArrayItem}
      hasSubmitted={currentCheckinMutation.isSuccess}
      addOneMoreLine={addArrayItem}
      onAnswerHandler={onAnswerHandler}
      onCopyPriorityHandler={onCopyPriorityHandler}
      onSubmitHandler={onSubmitHandler}
      questions={questions}
      isFormSubmitted={isFormSubmitted}
      prioritiesFromLastWeek={prioritiesFromLastWeek}
      priorityQuestion={priorityQuestion}
      handleUpdatePriorityStatus={handleUpdatePriorityStatus}
    />
  );
};

export default memo(UserCurrentCheckinContainer);
